import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MethodologyCard = ({ methodology, onCompare, isSelected, onSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getRiskColor = (level) => {
    switch (level) {
      case 'low': return 'text-success bg-success/10';
      case 'medium': return 'text-warning bg-warning/10';
      case 'high': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-success bg-success/10';
      case 'under-review': return 'text-warning bg-warning/10';
      case 'deprecated': return 'text-error bg-error/10';
      case 'revised': return 'text-accent bg-accent/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <div className={`bg-card border rounded-lg p-4 transition-smooth hover:shadow-subtle ${
      isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border'
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <h3 className="font-semibold text-card-foreground truncate">
              {methodology?.name}
            </h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(methodology?.status)}`}>
              {methodology?.status}
            </span>
          </div>
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <span>{methodology?.registry}</span>
            <span>•</span>
            <span>Version {methodology?.version}</span>
            <span>•</span>
            <span>{methodology?.type}</span>
          </div>
        </div>
        <div className="flex items-center space-x-2 ml-4">
          <button
            onClick={() => onSelect(methodology?.id)}
            className={`p-1 rounded transition-smooth ${
              isSelected ? 'text-primary' : 'text-muted-foreground hover:text-card-foreground'
            }`}
          >
            <Icon name={isSelected ? 'CheckSquare' : 'Square'} size={16} />
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-muted-foreground hover:text-card-foreground rounded transition-smooth"
          >
            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
          </button>
        </div>
      </div>
      {/* Risk and Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div className="text-center">
          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(methodology?.riskLevel)}`}>
            <Icon name="AlertTriangle" size={12} className="mr-1" />
            {methodology?.riskLevel} Risk
          </div>
        </div>
        <div className="text-center">
          <div className="text-xs text-muted-foreground">Rating</div>
          <div className="font-semibold text-card-foreground">{methodology?.rating}/10</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-muted-foreground">Credits Issued</div>
          <div className="font-semibold text-card-foreground">{methodology?.creditsIssued?.toLocaleString()}</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-muted-foreground">Avg Price</div>
          <div className="font-semibold text-card-foreground">${methodology?.avgPrice}</div>
        </div>
      </div>
      {/* Flags and Alerts */}
      {methodology?.flags && methodology?.flags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {methodology?.flags?.map((flag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-1 bg-warning/10 text-warning rounded text-xs"
            >
              <Icon name="Flag" size={10} className="mr-1" />
              {flag}
            </span>
          ))}
        </div>
      )}
      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-border pt-3 mt-3 space-y-3">
          {/* Description */}
          <div>
            <h4 className="text-sm font-medium text-card-foreground mb-1">Description</h4>
            <p className="text-sm text-muted-foreground">{methodology?.description}</p>
          </div>

          {/* Revision History */}
          <div>
            <h4 className="text-sm font-medium text-card-foreground mb-2">Recent Revisions</h4>
            <div className="space-y-2">
              {methodology?.revisions?.slice(0, 3)?.map((revision, index) => (
                <div key={index} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    v{revision?.version} - {revision?.date}
                  </span>
                  <span className="text-card-foreground">{revision?.changes}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Peer Reviews */}
          <div>
            <h4 className="text-sm font-medium text-card-foreground mb-2">Peer Reviews</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {methodology?.peerReviews?.map((review, index) => (
                <div key={index} className="bg-muted/50 rounded p-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-card-foreground">{review?.reviewer}</span>
                    <span className="text-xs text-muted-foreground">{review?.date}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex">
                      {[...Array(5)]?.map((_, i) => (
                        <Icon
                          key={i}
                          name="Star"
                          size={10}
                          className={i < review?.rating ? 'text-warning' : 'text-muted-foreground'}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">{review?.rating}/5</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                iconName="ExternalLink"
                iconPosition="right"
                onClick={() => window.open(methodology?.verraLink, '_blank')}
              >
                View on Verra
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
              >
                Export Analysis
              </Button>
            </div>
            <Button
              variant="secondary"
              size="sm"
              iconName="GitCompare"
              iconPosition="left"
              onClick={() => onCompare(methodology)}
            >
              Compare
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MethodologyCard;